/**
 * Zod schemas — the single source of truth for input validation.
 * Used on the server (Fastify) and the client (React forms); infer types with
 * `z.infer<typeof X>` rather than declaring them by hand.
 */

import { z } from 'zod';
import { LOCALES, MAX_ADDITIONAL_DOMAINS } from './constants';
import { normalizeDomain, validateDomain } from './utils';

export const emailSchema = z.string().trim().toLowerCase().email().max(255);

export const passwordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .max(128, 'Password must be at most 128 characters');

export const localeSchema = z.enum(LOCALES);

// --- Auth ------------------------------------------------------------------

export const signupSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
  name: z.string().trim().min(1).max(100).optional(),
  locale: localeSchema.optional(),
});
export type SignupInput = z.infer<typeof signupSchema>;

export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'Password is required').max(128),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const passwordResetRequestSchema = z.object({
  email: emailSchema,
});
export type PasswordResetRequestInput = z.infer<typeof passwordResetRequestSchema>;

export const passwordResetConfirmSchema = z.object({
  token: z.string().min(10).max(255),
  password: passwordSchema,
});
export type PasswordResetConfirmInput = z.infer<typeof passwordResetConfirmSchema>;

export const verifyEmailCodeSchema = z.object({
  code: z.string().regex(/^\d{6}$/, 'Enter the 6-digit code'),
});
export type VerifyEmailCodeInput = z.infer<typeof verifyEmailCodeSchema>;

// --- Profile ---------------------------------------------------------------

export const updateProfileSchema = z.object({
  name: z.string().trim().min(1).max(100).optional(),
  locale: localeSchema.optional(),
});
export type UpdateProfileInput = z.infer<typeof updateProfileSchema>;

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1).max(128),
  newPassword: passwordSchema,
});
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;

// --- Contact (landing) -----------------------------------------------------

export const contactSchema = z.object({
  name: z.string().trim().min(1).max(100),
  email: emailSchema,
  message: z.string().trim().min(10).max(5000),
});
export type ContactInput = z.infer<typeof contactSchema>;

// --- Projects --------------------------------------------------------------

/** A bare domain; normalised (protocol/path/www stripped) then validated. */
export const domainSchema = z
  .string()
  .trim()
  .min(1)
  .max(253)
  .transform(normalizeDomain)
  .refine((domain) => validateDomain(domain), 'Enter a valid domain (e.g. example.com)');

export const projectSettingsSchema = z.object({
  anonymizeIp: z.boolean(),
  alertThresholds: z.object({
    spike: z.number().int().min(0).max(1_000_000),
    score: z.number().int().min(0).max(100),
  }),
});
export type ProjectSettingsInput = z.infer<typeof projectSettingsSchema>;

export const createProjectSchema = z.object({
  name: z.string().trim().min(1).max(100),
  primaryDomain: domainSchema,
  additionalDomains: z.array(domainSchema).max(MAX_ADDITIONAL_DOMAINS).default([]),
});
export type CreateProjectInput = z.infer<typeof createProjectSchema>;

export const updateProjectSchema = z
  .object({
    name: z.string().trim().min(1).max(100),
    primaryDomain: domainSchema,
    additionalDomains: z.array(domainSchema).max(MAX_ADDITIONAL_DOMAINS),
    settings: projectSettingsSchema,
  })
  .partial();
export type UpdateProjectInput = z.infer<typeof updateProjectSchema>;

// --- Cloudflare (Palier D) -------------------------------------------------

/** The API token the client pastes (Cloudflare tokens are ~40 chars). */
export const cloudflareTokenSchema = z.object({
  token: z.string().trim().min(20).max(200),
});
export type CloudflareTokenInput = z.infer<typeof cloudflareTokenSchema>;

/** Persist the connection: token + chosen zone + initial (non-blocking) config. */
export const cloudflareConnectSchema = z.object({
  token: z.string().trim().min(20).max(200),
  zoneId: z.string().trim().min(1).max(64),
  mode: z.enum(['manual', 'challenge', 'auto']).optional(),
  threshold: z.number().int().min(0).max(100).optional(),
  manualAllowed: z.boolean().optional(),
});
export type CloudflareConnectInput = z.infer<typeof cloudflareConnectSchema>;

/** Provision the list + WAF rule (or fallback). Without `confirm` → dry-run only. */
export const cloudflareProvisionSchema = z.object({
  action: z.enum(['managed_challenge', 'block']).optional(),
  confirm: z.boolean().optional(),
});
export type CloudflareProvisionInput = z.infer<typeof cloudflareProvisionSchema>;

/** Disconnect: forget the token only, or also remove the Silospot-marked resources. */
export const cloudflareDisconnectSchema = z.object({
  mode: z.enum(['token-only', 'with-resources']).optional(),
});
export type CloudflareDisconnectInput = z.infer<typeof cloudflareDisconnectSchema>;

/** Run a blocklist sync. Without `confirm` → dry-run (plan only). */
export const cloudflareSyncSchema = z.object({
  confirm: z.boolean().optional(),
});
export type CloudflareSyncInput = z.infer<typeof cloudflareSyncSchema>;

/** Manual block of one IP/CIDR. Action defaults to managed_challenge (never block). */
export const cloudflareBlockSchema = z.object({
  ip: z.string().trim().min(3).max(64),
  action: z.enum(['managed_challenge', 'block']).optional(),
});
export type CloudflareBlockInput = z.infer<typeof cloudflareBlockSchema>;

/** Manual unblock of one IP/CIDR. */
export const cloudflareUnblockSchema = z.object({
  ip: z.string().trim().min(3).max(64),
});
export type CloudflareUnblockInput = z.infer<typeof cloudflareUnblockSchema>;

/** Bulk block: validate a pasted list (parse + dedup + whitelist + active check). */
export const cloudflareBulkValidateSchema = z.object({
  raw: z.string().max(200_000),
});
/** Bulk block/unblock: execute one CHUNK of already-validated targets (≤ 50). */
export const cloudflareBulkExecSchema = z.object({
  targets: z.array(z.string().trim().min(3).max(64)).min(1).max(50),
});

export type BulkKind = 'ipv4' | 'ipv4_range' | 'ipv6_household' | 'ipv6_range';
export interface BulkValidateResult {
  /** Ready-to-block, deduplicated, not whitelisted, not already active. */
  valid: { input: string; target: string; kind: BulkKind }[];
  /** Unrecognised or Cloudflare-unsupported lines (with a plain reason). */
  invalid: { input: string; reason: string }[];
  /** Skipped because already blocked at the edge. */
  alreadyBlocked: string[];
  /** Skipped because on the real_client whitelist (sovereign). */
  whitelisted: string[];
  counts: { valid: number; invalid: number; alreadyBlocked: number; whitelisted: number };
}
export interface BulkExecResult {
  results: { target: string; ok: boolean; error?: string }[];
  listCount: number;
}

/**
 * Enable/disable the auto-block scheduler for a connection. `enabled` toggles
 * mode auto↔manual; `dryRun` keeps it computing-only (nothing pushed to the edge)
 * until the client promotes it to live; `threshold` is the push score (default 80).
 */
export const cloudflareAutoSchema = z.object({
  enabled: z.boolean(),
  dryRun: z.boolean().optional(),
  threshold: z.number().int().min(50).max(100).optional(),
});
export type CloudflareAutoInput = z.infer<typeof cloudflareAutoSchema>;

// --- GTM tracker install (Palier E) ----------------------------------------

/** Install (or update) the Silospot tag in a container. Without `confirm` → dry-run. */
export const gtmInstallSchema = z.object({
  accountId: z.string().trim().min(1).max(40),
  containerId: z.string().trim().min(1).max(40),
  confirm: z.boolean().optional(),
});
export type GtmInstallInput = z.infer<typeof gtmInstallSchema>;
